import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Loader2 } from "lucide-react";
import { API, authHdr } from "./AssociateFields";

const M = `${API}/legacy-migration`;

const Stat = ({ label, value, tid }) => (
  <div className="rounded-md border bg-slate-50 px-3 py-2" data-testid={tid}>
    <div className="text-xs uppercase text-slate-500">{label}</div><div className="text-lg font-semibold">{value}</div>
  </div>
);

const ReportView = ({ rep }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Stat label="Associates" value={rep.totals.associates} tid="mig-total-associates" />
      <Stat label="Matched existing" value={rep.totals.matched} tid="mig-total-matched" />
      <Stat label="Documents" value={`${rep.totals.documents} + ${rep.totals.older_versions} older`} tid="mig-total-docs" />
      <Stat label="Size" value={`${rep.totals.documents_mb} MB`} tid="mig-total-size" />
    </div>
    <p className="text-xs text-slate-600">Statuses: {Object.entries(rep.by_status).map(([k, v]) => `${k} ${v}`).join(" · ")}
      {rep.skipped.length ? ` · Skipped ${rep.skipped.length} (empty draft)` : ""}</p>
    <div className="max-h-[42vh] overflow-auto rounded-md border">
      <table className="w-full text-xs" data-testid="mig-report-table">
        <thead className="sticky top-0 bg-white"><tr className="border-b text-left uppercase text-slate-500">
          <th className="px-2 py-1.5">Name</th><th className="px-2">Code</th><th className="px-2">Old status</th><th className="px-2">Docs</th><th className="px-2">Notes</th></tr></thead>
        <tbody>{rep.rows.map((r) => (
          <tr key={r.legacy_id} className={`border-b last:border-0 ${r.warnings.length ? "bg-amber-50" : ""}`}>
            <td className="px-2 py-1.5 font-medium">{r.name}</td><td className="px-2">{r.new_code}</td><td className="px-2">{r.old_status}</td>
            <td className="px-2">{r.documents}{r.older_versions ? ` +${r.older_versions}` : ""}</td>
            <td className="px-2 text-amber-800">{r.warnings.join("; ")}</td>
          </tr>))}</tbody>
      </table>
    </div>
  </div>
);

const JobView = ({ job }) => {
  const total = (job.total_records || 0) + (job.total_docs || 0);
  const done = (job.done_records || 0) + (job.done_docs || 0);
  return (
    <div className="space-y-2" data-testid="mig-job-status">
      <div className="flex items-center justify-between text-sm"><span>{job.step}</span>
        <Badge variant="outline" data-testid="mig-job-state">{job.status}</Badge></div>
      <Progress value={total ? (done / total) * 100 : 5} />
      <p className="text-xs text-slate-600">Associates {job.done_records}/{job.total_records} · Documents {job.done_docs}/{job.total_docs}</p>
      {!!job.errors?.length && <ul className="max-h-32 overflow-auto text-xs text-red-700" data-testid="mig-job-errors">{job.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
    </div>
  );
};

export const LegacyMigrationDialog = ({ open, onClose, onDone }) => {
  const [rep, setRep] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const poll = async () => {
    const r = await axios.get(`${M}/status`, { headers: authHdr() });
    setJob(r.data.job);
    if (r.data.job?.status === "running") timer.current = setTimeout(poll, 3000);
    else if (r.data.job?.status === "completed") onDone?.();
  };
  useEffect(() => { if (open) poll().catch(() => {}); return () => clearTimeout(timer.current); }, [open]); // eslint-disable-line

  const check = async () => {
    setBusy(true);
    try { setRep((await axios.get(`${M}/report`, { headers: authHdr(), timeout: 180000 })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read old system"); } finally { setBusy(false); }
  };
  const start = async () => {
    if (!window.confirm("Move all associates and documents from old Kinntegra now? Existing details are kept; only blanks are filled.")) return;
    try { setJob((await axios.post(`${M}/run`, {}, { headers: authHdr() })).data); timer.current = setTimeout(poll, 2000); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };
  const running = job?.status === "running";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="mig-dialog">
        <DialogHeader><DialogTitle>Move data from old Kinntegra</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Step 1: check the report. Step 2: start the move. Matched associates keep their current details and code; only blank fields are filled and documents are attached. Safe to run again.</p>
        {rep && <ReportView rep={rep} />}
        {job && <JobView job={job} />}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={check} disabled={busy || running} data-testid="mig-check-btn">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{rep ? "Refresh report" : "Check old data"}</Button>
          <Button onClick={start} disabled={!rep || running} data-testid="mig-start-btn">
            {running && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{job?.status === "completed" ? "Run again" : "Start move"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
